import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACES_COST_SUMMARY_REPORT_QUERY_KEY =
  'namespaces-cost-summary-report';

const getNamespacesCostSummaryQueryKey = ({
  startTime,
  endTime,
  clusterId,
  useListingPrices,
  sortBy,
  sortDirection,
  pageLimit,
  includeIdleCosts,
}: Partial<Props> = {}) =>
  [
    NAMESPACES_COST_SUMMARY_REPORT_QUERY_KEY,
    ...(startTime !== undefined
      ? [
          startTime,
          endTime,
          clusterId,
          useListingPrices,
          sortBy,
          sortDirection,
          pageLimit,
          includeIdleCosts,
        ]
      : []),
  ] as const;

type Props = {
  startTime: string;
  endTime: string;
  clusterId: string;
  useListingPrices: boolean;
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getNamespacesCostSummaryQueryOptions = ({
  startTime,
  endTime,
  clusterId,
  useListingPrices,
  sortBy,
  sortDirection,
  pageLimit = 50,
  enabled = true,
  includeIdleCosts = false,
}: Props) =>
  infiniteQueryOptions({
    queryKey: getNamespacesCostSummaryQueryKey({
      startTime,
      endTime,
      clusterId,
      useListingPrices,
      sortBy,
      sortDirection,
      pageLimit,
      includeIdleCosts,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getNamespacesCostSummary({
        startTime,
        endTime,
        clusterId,
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortBy,
        sortOrder: sortDirection,
        filter: {},
        useListingPrices,
        includeIdleResourceCosts: includeIdleCosts,
      });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useNamespacesCostSummaryQuery = (props: Props) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getNamespacesCostSummaryQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
    noDataReason: data?.pages[0].noDataReason ?? '',
  };
};
